import React, { useEffect, useState } from 'react';

import CheckmarkCircle from '@icons/checkmark-circle-filled.svg';
import Heading from '@components/Heading';
import Box from '@components/Box';
import Button from '@components/Button';
import { useRouter } from 'next/router';
import { useGetInvoiceQuery } from '@features/Checkout/services/checkout';
import { useSelector, useDispatch } from 'react-redux';
import LoadingIcon from '@components/LoadingIcon';
import NextLink from 'next/link';
import HTMLText from '@features/PageBuilder/components/HTMLText';
import Link from '@components/Link';
import {
  setIsBusiness,
  setOrderId,
  setTerminalSession,
} from '@features/Checkout/store';
import { useTranslation } from 'next-i18next';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { useFormatPrice } from '@components/Price';
import useDateTime from '@hooks/use-datetime';
import InvoiceOrderItems from './InvoiceOrderItems';
import {
  Root,
  Header as HeaderClass,
  CheckmarkIcon as CheckmarkIconClass,
  Box as BoxClass,
  Muted,
  ButtonWrapper,
  Text,
  WhiteHeaderBox,
  OrderItems,
  OrderItem as OrderItemClass,
  SummaryPriceInfo,
} from './Confirmation.module.css';
import {
  pushCampaignToDataLayer,
  pushPurchaseToDataLayer,
} from '@utilities/helpers/common';

const Confirmation = ({ goToStep }) => {
  const { t } = useTranslation(['common', 'checkout', 'account']);
  const router = useRouter();
  const dispatch = useDispatch();
  const [isInfoNotFilled, setIsInfoNotFilled] = useState(true);
  const {
    query: {
      orderId: orderIdFromQuery,
      newOrder = false,
      terminalSession: terminalSessionFromQuery,
      campaignId,
    },
  } = router;

  /* todo: get isBusiness from invoice response instead of checkout state */
  const {
    orderId: orderIdFromState,
    isBusiness,
    terminalSession: terminalSessionFromState,
    couponCode,
  } = useSelector((state) => state.checkout);

  const {
    options: { priceModels, countryCode, currencyCode },
  } = useSelector((state) => state.wordpress);

  const orderId = orderIdFromQuery || orderIdFromState;
  const terminalSession = terminalSessionFromQuery || terminalSessionFromState;

  const showsPriceChanges =
    priceModels?.find((priceWarning) => priceWarning.priceWarning)
      ?.priceWarning !== '' && !['DE'].includes(countryCode);
  const priceChangesText = priceModels?.find(
    (priceWarning) => priceWarning.priceWarning
  )?.priceWarning;

  const { data: order = null, isLoading } = useGetInvoiceQuery(
    { id: orderId, vat: isBusiness, campaignId },
    {
      skip: !orderId,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (!terminalSession) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer?.push({
      event: 'statsig.pos.conversion',
      posSessionId: terminalSession,
      sessionId: sessionStorage.getItem('sessionId'),
    });
    dispatch(setTerminalSession(null));
  }, [dispatch, terminalSession]);

  const amount = useFormatPrice({
    amount: order?.total.nextMonthsPayment / 100,
    noCurrencySymbol: true,
    showDecimals: true,
  });
  const minAmount = useFormatPrice({
    amount: order?.minAmount / 100,
    noCurrencySymbol: true,
    showDecimals: true,
  });
  const planPrice = useFormatPrice({
    amount: order?.planPrice / 100,
    noCurrencySymbol: true,
    showDecimals: true,
  });
  const { format } = useDateTime();

  // If customer has vat number set state isBusiness to true
  useEffect(() => {
    if (order?.customerInfo.vat) dispatch(setIsBusiness(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  useEffect(() => {
    if (isLoading || !order) {
      return;
    }

    let isEmpty = Object.entries(order.customerInfo).some(([k, v]) => {
      if (k !== 'vat') {
        return v === null;
      }
      return false;
    });
    isEmpty = Object.entries(order.customerAddress).some(([k, v]) => {
      if (!['company', 'country'].includes(k)) {
        return v === null;
      }
      return false;
    });
    setIsInfoNotFilled(isEmpty);
  }, [isLoading, order]);

  useEffect(() => {
    if (!orderId && router.isReady) {
      // console.error('no order id', orderId);
      goToStep(0);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!order || !orderIdFromState) return;

    pushPurchaseToDataLayer(
      orderId,
      order.customerInfo.newCustomer,
      order.items,
      order.total,
      currencyCode,
      couponCode,
      sessionStorage.getItem('sessionId')
    );
    dispatch(setOrderId(null));
  }, [order, orderIdFromState]);

  useEffect(() => {
    if (!order || !campaignId) return;

    pushCampaignToDataLayer(
      campaignId,
      order.campaignName,
      orderId,
      order.customerInfo.newCustomer,
      order.items,
      order.total,
      currencyCode,
      couponCode,
      sessionStorage.getItem('sessionId')
    );
  }, [order, campaignId]);

  const newsletter = !order?.subscriptions.marketing_20211201 && (
    <NextLink href="/my-account/information" legacyBehavior>
      {t('signUpForNews', { ns: 'checkout' })}
    </NextLink>
  );

  if (isLoading || !order) {
    return (
      <div>
        <LoadingIcon withWrapper />
      </div>
    );
  }

  return (
    <div className={Root}>
      <Heading size="medium" className={HeaderClass} hasHTML={false}>
        <CheckmarkCircle className={CheckmarkIconClass} />
        {t('receipt', { ns: 'common' })}
      </Heading>
      <Box appearance="white" className={BoxClass}>
        <Heading size="extra-small" className={WhiteHeaderBox}>
          {isBusiness
            ? t('CompanyInformation', { ns: 'account' })
            : t('yourInformation', { ns: 'account' })}
        </Heading>
        <table>
          <tbody>
            <tr>
              <td>{t('email', { ns: 'common' })}:</td>
              <td>{order?.customerInfo?.email}</td>
            </tr>
            <tr>
              <td>{t('mobile', { ns: 'common' })}:</td>
              <td>
                {formatPhoneNumberIntl(
                  `+${order?.customerInfo?.phone_prefix}${order?.customerInfo?.phone}`
                )}
              </td>
            </tr>
            {isBusiness && (
              <>
                <tr>
                  <td>{t('vat', { ns: 'common' })}:</td>
                  <td>{order?.customerInfo?.vat}</td>
                </tr>
                <tr>
                  <td>{t('company', { ns: 'common' })}:</td>
                  <td>{order?.customerAddress?.company}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        <div className={Muted}>{newsletter}</div>
        <div className={ButtonWrapper}>
          {isInfoNotFilled ? (
            <Button
              element={Link}
              appearance="primary"
              fullWidth
              href="/my-account/information"
            >
              {t('finishProfile', { ns: 'account' })}
            </Button>
          ) : (
            <Button
              element={Link}
              appearance="primary"
              fullWidth
              href="/my-account/overview"
            >
              {t('goToProfile', { ns: 'checkout' })}
            </Button>
          )}
        </div>
      </Box>
      {newOrder && (
        <Box appearance="white" className={BoxClass}>
          <Heading size="extra-small" className={WhiteHeaderBox}>
            {t('yourPincode', { ns: 'checkout' })}
          </Heading>
          <div className={Text}>
            {t('plateRecognition', { ns: 'checkout' })}
          </div>
          <table>
            <tbody>
              <tr>
                <td>
                  <b>{t('pincode', { ns: 'common' })}:</b>
                </td>
                <td>
                  <b>{order?.customerInfo?.pin}</b>
                </td>
              </tr>
            </tbody>
          </table>
          <div className={ButtonWrapper}>
            <Button
              element={Link}
              appearance="secondary"
              href="/my-account/password"
              fullWidth
            >
              {t('changePincode', { ns: 'account' })}
            </Button>
          </div>
        </Box>
      )}
      <div className={OrderItems}>
        {order?.preOrderItems && (
          <>
            <div className={OrderItemClass}>
              <InvoiceOrderItems
                items={order.preOrderItems}
                orderPrice={order.preOrderTotal}
                isPreOrder
              />
            </div>
            <div className={SummaryPriceInfo}>
              {t('monthlyPriceAndTerminationDatePreOrder', {
                ns: 'checkout',
                date: t('startDate', { ns: 'checkout' }).toLocaleLowerCase(),
                amount,
              })}
            </div>
          </>
        )}
        {order?.items && (
          <>
            <div className={OrderItemClass}>
              <InvoiceOrderItems items={order.items} orderPrice={order.total} />
            </div>
            {!campaignId && (
              <div className={SummaryPriceInfo}>
                {t('monthlyPriceAndTerminationDate', {
                  ns: 'checkout',
                  date: format(
                    order?.total.firstPossibleTerminationDate,
                    'dd.MM.yyyy'
                  ),
                  amount,
                })}
              </div>
            )}
            {campaignId && order?.terminationRule === 2 && (
              <div className={SummaryPriceInfo}>
                {t('monthlyPriceAndTerminationDateCampaign', {
                  ns: 'checkout',
                  first_termination: format(
                    order?.firstTermination,
                    'dd.MM.yyyy'
                  ),
                  min_amount: minAmount,
                  plan_price: planPrice,
                })}
              </div>
            )}
          </>
        )}

        {showsPriceChanges && (
          <div className={SummaryPriceInfo}>
            <HTMLText>{priceChangesText}</HTMLText>
          </div>
        )}
      </div>
      {isBusiness && (
        <center>{t('priceExcludeVAT', { ns: 'checkout' })}</center>
      )}
    </div>
  );
};

export default Confirmation;
